<template>
  <Modal ref="modalRef" class="sharing-limit-modal" info @shown="onShown">
    <template #title>
      Current sharing
    </template>
    <div v-if="sharingLimitReachedRef" class="alert alert-primary alert-sm">
      You have reached your sharing limit.
      <router-link to="/plans">Upgrade</router-link>
    </div>
    <div class="sharing-limit">
      <div>Sharing limit:</div>
      <div class="value">
        <span class="count">{{ sharingLimitRef }}</span> users
      </div>
      <div>Currently sharing with:</div>
      <div class="value">
        <span class="count">{{ sharingUsersRef.length }}</span> users
      </div>
      <div class="help-text-sm">
        This is the total number of unique users or invited users in all the groups you own, 
        except groups that are only sharing trees that are "free to share".
      </div>
    </div>
    <ul class="list-unstyled">
      <li v-for="u in sharingUsersRef" :key="u.userId ?? u.inviteId!" :class="{ invited: u.invited }">
        <div>
          <img v-if="u.invited" src="@/assets/icons/mail_gray_60.png" class="invite-icon"/>
          <img src="@/assets/icons/person_gray_150.png" class="user-icon">
          {{ u.displayName }}
        </div>
      </li>
    </ul>
    <template #secondaryText>
      Learn about sharing limits
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
:deep(.modal-body) {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.alert {
  margin: 0;
  a { 
    margin-left: 0.25rem;
  }
}

.sharing-limit {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 1.5rem;
  align-items: baseline;

  @media (min-width: 768px) {
    padding: 0 1rem;
  }

  .value {
    font-size: 0.875rem;
    color: #888;
  }

  .count {
    color: black;
    font-size: 1.5rem;
  }

  .remaining {
    font-size: 0.875rem;
  }

  .help-text-sm {
    grid-column: 1 / span 2;
  }
}

ul {
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (min-width: 768px) {
    margin-left: 3rem;
  }

  li {
    position: relative;

    .invite-icon {
      position: absolute;
      top: 4px;
      left: -1.5rem;
      width: 1.2rem;
      filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
    }

    .user-icon {
      width: 1.2rem;
      opacity: 60%;
    }

    &.invited .user-icon {
      opacity: 30%;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { SharingUser, useSharingLimiter } from './SharingLimiter'
import { useModalAutoHide } from '@/util/AppUtil'
import Modal from '@/util/Modal.vue'

const sharingUsersRef = ref<SharingUser[]>([])
const modalRef = ref<InstanceType<typeof Modal>>()

const userStore = useUserStore()
const sharingLimiter = useSharingLimiter()

useModalAutoHide(modalRef)

const selfUserRef = computed(() => userStore.getAsyncSelf()?.data)
const sharingLimitRef = computed(() => selfUserRef.value?.limits?.sharingLimit ?? 0)
const remainingRef = computed(() => Math.max(0, sharingLimitRef.value - sharingUsersRef.value.length))
const sharingLimitReachedRef = computed(() => sharingUsersRef.value.length >= sharingLimitRef.value)

async function onShown() {
  // ensure we have the right #'s each time the modal is shown
  sharingUsersRef.value = await sharingLimiter.getSharingUsersAsync()
  sharingUsersRef.value.sort((a, b) => a.displayName.localeCompare(b.displayName))
}

</script>
